import { Section } from './Section';
import { supplementsVisible } from '../utils/supplementsVisibility';

export type MarkersOutsideOptimalDisplay =
  | 'showBars'
  | 'hideBars'
  | 'hideSection'
  | 'minimizeSection';

export class UserProfile {
  id: string; // identity sub
  email: string;
  emailAlias: string;
  firstName: string; // Currently only available in PROD (different Auth strategy)
  lastName: string; // Currently only available in PROD (different Auth strategy)
  phoneNumber: string;
  userName: string;
  signupDate: string; // ISO-8601 format
  preparedBy?: string; // Optional field for "Prepared by" text

  // User Limitations
  numTests: number; // default is 0
  numTestsAllowed: number; // "unlimited" - the value should be 99,999 in DB, default is 2
  pdfExportAllowed: boolean;
  pdfExportComparisonTableAllowed: boolean;
  editClientAllowed: boolean;
  editTestResultsAllowed: boolean;
  showClientComparisonTable: boolean;
  showAnalyzeResultsComparison: boolean;
  uploadTestResults: boolean;
  showUploadTestResults: boolean;
  customLogoUrl: string;
  customLogoAllowed: boolean;
  enabledSupplementBrands: string[] = ['systemicFormulas'];

  // Brands the app has already auto-added to enabledSupplementBrands once (see
  // the constructor). A brand is seeded a single time so a newly-launched brand
  // reaches practitioners whose stored profile predates it; after that the
  // practitioner's own checkbox choice wins, including unchecking it.
  seededSupplementBrands: string[] = [];

  // Sections to exclude from the Analysis PDF by default. Empty (the default)
  // means every section is included. Each section header in Test Results Analysis
  // has its own "Include in PDF" checkbox; this list seeds those checkboxes on
  // mount. Per-test toggles are session-only and do not persist to this list.
  excludedFromPdfSections: Section[] = [];

  // Controls how the "Markers Outside Optimal" section renders by default:
  //   showBars        - the section is open, panels showing reference-range bars
  //                     (default)
  //   hideBars        - the section is open with each panel's charts collapsed;
  //                     user can click a panel's chevron to reveal its bars for
  //                     that session
  //   hideSection     - the entire section is hidden in the UI
  //   minimizeSection - the section renders collapsed to its header; clicking it
  //                     expands to the 'showBars' layout
  //
  // All four are screen-only. The PDF renders the section for every mode except
  // 'hideSection' (see bloodwork-analysis-pdf.tsx) — a minimized section still
  // exports in full, and the per-section "Include in PDF" checkbox is what
  // controls that.
  markersOutsideOptimalDisplay: MarkersOutsideOptimalDisplay = 'showBars';

  // When true (default), the Inflammation section hides cross-section
  // markers (those with a home in another section, e.g. ferritin, GGT,
  // albumin, HDL) unless they contribute a signal there. Markers native
  // to the section (hs-CRP, ESR, homocysteine) always stay visible
  // regardless of value. If filtering empties the section, it disappears
  // entirely.
  showOnlySectionSignals: boolean = true;

  // When true (default), the Immune section hides cross-section markers
  // unless they contribute a signal there. Example: alkaline phosphatase
  // is hidden when optimal or high (only low Alk Phos signals low zinc,
  // which the immune system needs); globulin is hidden when optimal.
  // Markers native to the section (WBC and differentials) always stay
  // visible regardless of value.
  showOnlyImmuneSectionSignals: boolean = true;

  // When true (default), the Immune section shows the absolute differential
  // bar graphs (Absolute Neutrophils, Lymphocytes, Monocytes, Eosinophils,
  // Basophils). Uncheck to hide those bar graphs. This is independent of
  // showOnlyImmuneSectionSignals, which never hides the differentials.
  showImmuneAbsolutes: boolean = true;

  // When true (default), the Blood Work Comparison table and its PDF order
  // their sections to match the Test Results Analysis page. When false, they
  // use the legacy lab-report order (CBC markers, then CMP, then lipids, etc.).
  comparisonMatchAnalysisOrder: boolean = true;

  // When true (default), a comparison report (current vs a previous test) also
  // renders reference-range bars for markers that have a value in the PREVIOUS
  // test but NOT the current one, plotting just the previous (blue) blood drop.
  // This gives a complete side-by-side even when the current test dropped a
  // marker — otherwise those markers vanish from the charts while still driving
  // the Results Overview section colors and thumbs. Uncheck to show only
  // markers present in the current test. No effect when viewing a single test.
  showPreviousTestMarkersInComparison: boolean = true;

  canAddTest(): boolean {
    if (this.numTests < this.numTestsAllowed) {
      return true;
    }
    return false;
  }

  canUploadPdf(): boolean {
    return this.uploadTestResults;
  }

  canSeeUploadTestResults(): boolean {
    return this.showUploadTestResults;
  }

  canUploadCustomLogo(): boolean {
    return this.customLogoAllowed;
  }

  hasCustomLogo(): boolean {
    return (
      this.canUploadCustomLogo() &&
      !!this.customLogoUrl &&
      this.customLogoUrl.trim() !== ''
    );
  }

  getLogoUrl(): string {
    return this.canUploadCustomLogo() && this.hasCustomLogo()
      ? this.customLogoUrl
      : '/assets/imgs/logo.png';
  }

  incrementNumTests(): void {
    this.numTests++;
  }

  constructor(userProfile?: UserProfile) {
    // This handles copying the simple properties
    if (!userProfile) {
      return;
    }
    // This handles copying the simple properties
    Object.keys(userProfile).forEach(key => {
      this[key] = userProfile[key];
    });
    // BWH Labs is a pre-launch brand: shown wherever the supplement features
    // are, i.e. everywhere but prod (see supplementsVisible()), mirroring the
    // Supplement Settings block. Because any prior profile save persists
    // enabledSupplementBrands, the field default can't be relied on to reach
    // existing profiles — so normalize on load:
    //   dev/slava → seed 'bwhLabs' ONCE so it renders for every practitioner
    //   prod      → strip 'bwhLabs' so it never renders, even if a stored
    //               profile happens to carry it.
    // The seed is one-time and recorded in seededSupplementBrands: re-adding it
    // on every load would silently undo a practitioner who unchecks the brand,
    // so once seeded the stored enabledSupplementBrands is left alone.
    const bwhLabsVisible = supplementsVisible();
    if (!this.enabledSupplementBrands) {
      this.enabledSupplementBrands = ['systemicFormulas'];
    }
    if (!this.seededSupplementBrands) {
      this.seededSupplementBrands = [];
    }
    if (bwhLabsVisible) {
      if (
        !this.enabledSupplementBrands.includes('bwhLabs') &&
        !this.seededSupplementBrands.includes('bwhLabs')
      ) {
        this.enabledSupplementBrands = [
          ...this.enabledSupplementBrands,
          'bwhLabs',
        ];
      }
      if (!this.seededSupplementBrands.includes('bwhLabs')) {
        this.seededSupplementBrands = [
          ...this.seededSupplementBrands,
          'bwhLabs',
        ];
      }
    } else {
      this.enabledSupplementBrands = this.enabledSupplementBrands.filter(
        b => b !== 'bwhLabs',
      );
    }
  }
}
